// src/pages/OperationsIntelligence.js
// Rule-based Operations Intelligence Engine
// Reads live Firestore data and flags real business risks.

import React, { useState, useEffect } from 'react';
import {
  collection, query, orderBy, getDocs,
  where, Timestamp, limit
} from 'firebase/firestore';
import { db } from '../firebase';
import { generateIntelligenceReport } from '../utils/intelligenceExport';
import { SEV, runRules } from '../utils/intelligenceEngine';
import './OperationsIntelligence.css';

// ── Main component ────────────────────────────────────────────────────────────
function OperationsIntelligence({ user, onBack }) {
  const [flags, setFlags]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [lastRun, setLastRun]   = useState(null);
  const [expanded, setExpanded] = useState({});
  const [filterSev, setFilterSev] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('weekly');
  const [summary, setSummary]   = useState({ critical: 0, high: 0, medium: 0, positive: 0, total: 0 });

  useEffect(() => { runAnalysis(); }, []);

  const runAnalysis = async () => {
    setLoading(true);
    try {
      const day7 = new Date(Date.now() - 7 * 86400000);
      const day30 = new Date(Date.now() - 30 * 86400000);

      // ✅ FIXED: the comment here always said "last 30 days" but the
      // actual query had no date bound at all — it was fetching the
      // entire service history, every time this analysis ran. Now it
      // actually matches what it claims to do.
      const [servicesSnap, usersSnap, clockSnap] = await Promise.all([
        getDocs(query(
          collection(db, 'serviceRecords'),
          where('createdAt', '>=', Timestamp.fromDate(day30)),
          orderBy('createdAt', 'desc'),
          limit(3000)
        )),
        getDocs(collection(db, 'users')),
        getDocs(query(
          collection(db, 'clockEvents'),
          where('timestamp', '>=', day30.getTime()),
          orderBy('timestamp', 'desc'),
          limit(3000)
        ))
      ]);

      const services    = servicesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const users       = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const clockEvents = clockSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const result = runRules({ services, clockEvents, users });
      setFlags(result);
      setLastRun(new Date());
      setSummary({
        critical: result.filter(f => f.severity === SEV.CRITICAL).length,
        high:     result.filter(f => f.severity === SEV.HIGH).length,
        medium:   result.filter(f => f.severity === SEV.MEDIUM).length,
        positive: result.filter(f => f.severity === SEV.POSITIVE).length,
        total:    result.filter(f => f.severity !== SEV.POSITIVE).length
      });
    } catch (e) {
      console.error('Intelligence engine error:', e);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (i) => setExpanded(p => ({ ...p, [i]: !p[i] }));

  const filtered = flags.filter(f => {
    if (filterSev === 'all')      return true;
    if (filterSev === 'critical') return f.severity === SEV.CRITICAL;
    if (filterSev === 'high')     return f.severity === SEV.HIGH;
    if (filterSev === 'medium')   return f.severity === SEV.MEDIUM;
    if (filterSev === 'positive') return f.severity === SEV.POSITIVE;
    return true;
  });

  const handleExport = async (period) => {
    setExporting(true);
    try {
      await generateIntelligenceReport(flags, summary, period);
    } catch (e) {
      alert('Export failed. Please try again.');
      console.error(e);
    } finally {
      setExporting(false);
    }
  };

  const formatTime = (d) => d ? d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' }) : '';

  // ── Render ────────────────────────────────────────────────────────────────
  const verdictSevKey = summary.critical > 0 ? 'critical' : summary.high > 0 ? 'high' : 'positive';

  return (
    <div className="ix-page">

      {/* Header */}
      <div className="ix-header">
        <button className="back-button" onClick={onBack}>← Back</button>
        <div className="ix-header-center">
          <h1 className="ix-header-title">🧠 Operations Intelligence</h1>
          {lastRun && (
            <span className="ix-last-run">Last run {formatTime(lastRun)}</span>
          )}
        </div>
        <div className="ix-header-actions">
          <button className="ix-refresh-btn" onClick={runAnalysis} disabled={loading}>
            {loading ? '⏳' : '🔄'}
          </button>
          {/* Export dropdown */}
          {!loading && flags.length > 0 && (
            <div className="ix-export-wrap">
              <button
                className="ix-export-btn"
                onClick={() => setExportPeriod(p => p === 'open' ? 'weekly' : 'open')}
                disabled={exporting}
              >
                {exporting ? '⏳ Exporting...' : '📄 Export PDF'}
              </button>
              {exportPeriod === 'open' && (
                <div className="ix-export-menu">
                  {[
                    { key: 'weekly',   label: '📅 Weekly Report'   },
                    { key: 'biweekly', label: '📅 Bi-Weekly Report' },
                    { key: 'monthly',  label: '📅 Monthly Report'   }
                  ].map(opt => (
                    <button
                      key={opt.key}
                      className="ix-export-menu-item"
                      onClick={() => { setExportPeriod('weekly'); handleExport(opt.key); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="ix-loading-wrap">
          <div className="ix-spinner" />
          <p className="ix-loading-text">Analysing operations data...</p>
          <p className="ix-loading-sub">Checking {'>'}7 days of service records, clock data and technician performance</p>
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="ix-summary-row">
            <div className="ix-sumcard sev-critical">
              <div className="ix-sum-icon">🔴</div>
              <div className="ix-sum-num">{summary.critical}</div>
              <div className="ix-sum-label">Critical</div>
            </div>
            <div className="ix-sumcard sev-high">
              <div className="ix-sum-icon">🟠</div>
              <div className="ix-sum-num">{summary.high}</div>
              <div className="ix-sum-label">High</div>
            </div>
            <div className="ix-sumcard sev-medium">
              <div className="ix-sum-icon">🟡</div>
              <div className="ix-sum-num">{summary.medium}</div>
              <div className="ix-sum-label">Medium</div>
            </div>
            <div className="ix-sumcard sev-positive">
              <div className="ix-sum-icon">✅</div>
              <div className="ix-sum-num">{summary.positive}</div>
              <div className="ix-sum-label">Positive</div>
            </div>
          </div>

          {/* Overall verdict */}
          <div className={`ix-verdict sev-${verdictSevKey}`}>
            <span className="ix-verdict-icon">
              {summary.critical > 0 ? '🚨' : summary.high > 0 ? '⚠️' : '✅'}
            </span>
            <div>
              <div className="ix-verdict-title">
                {summary.critical > 0
                  ? `${summary.critical} critical issue${summary.critical > 1 ? 's' : ''} require immediate attention`
                  : summary.high > 0
                  ? `${summary.high} high-priority issue${summary.high > 1 ? 's' : ''} need addressing this week`
                  : 'Operations are running well — keep the standard up'}
              </div>
              <div className="ix-verdict-sub">
                Based on last 7 days — {flags.filter(f => f.severity !== SEV.POSITIVE).length} risk{flags.filter(f => f.severity !== SEV.POSITIVE).length !== 1 ? 's' : ''} identified across {new Set(flags.map(f => f.technician).filter(Boolean)).size} technician{new Set(flags.map(f => f.technician).filter(Boolean)).size !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Filter pills */}
          <div className="ix-filter-row">
            {[
              { key: 'all',      label: `All (${flags.length})`              },
              { key: 'critical', label: `🔴 Critical (${summary.critical})`  },
              { key: 'high',     label: `🟠 High (${summary.high})`          },
              { key: 'medium',   label: `🟡 Medium (${summary.medium})`      },
              { key: 'positive', label: `✅ Positive (${summary.positive})`  }
            ].map(f => (
              <button key={f.key}
                className={`ix-filter-pill ${filterSev === f.key ? 'active' : ''}`}
                onClick={() => setFilterSev(f.key)}>
                {f.label}
              </button>
            ))}
          </div>

          {/* Flags list */}
          <div className="ix-list">
            {filtered.length === 0 ? (
              <div className="ix-empty">
                <div className="ix-empty-icon">✅</div>
                <h3 className="ix-empty-title">No issues in this category</h3>
                <p className="ix-empty-sub">Your operations are clean here</p>
              </div>
            ) : (
              filtered.map((flag, i) => {
                const sev  = flag.severity;
                const open = expanded[i];
                return (
                  <div key={i} className={`ix-flag-card sev-${sev.key}`}>
                    {/* Top row */}
                    <div className="ix-flag-top" onClick={() => toggleExpand(i)}>
                      <div className="ix-flag-left">
                        <span className="ix-flag-sev-icon">{sev.icon}</span>
                        <div>
                          <div className="ix-flag-meta">
                            <span className="ix-sev-badge">
                              {sev.label}
                            </span>
                            <span className="ix-cat-badge">{flag.category}</span>
                            {flag.technician && (
                              <span className="ix-tech-badge">👤 {flag.technician}</span>
                            )}
                          </div>
                          <p className="ix-flag-title">{flag.title}</p>
                          <p className="ix-flag-metric">{flag.metric}</p>
                        </div>
                      </div>
                      <button className="ix-expand-btn">
                        {open ? '▲' : '▼'}
                      </button>
                    </div>

                    {/* Expanded detail */}
                    {open && (
                      <div className="ix-flag-detail">
                        <div className="ix-detail-section">
                          <div className="ix-detail-label">📋 ANALYSIS</div>
                          <p className="ix-detail-text">{flag.detail}</p>
                        </div>
                        <div className="ix-detail-section action">
                          <div className="ix-detail-label">⚡ RECOMMENDED ACTION</div>
                          <p className="ix-detail-text strong">{flag.action}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer note */}
          <div className="ix-footer">
            <p className="ix-footer-text">
              🧠 Analysis based on last 7 days of live data.
              Rules are applied without tolerance for laxity or business loss.
              Tap any flag to see full analysis and recommended action.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default OperationsIntelligence;
